<template>
  <div id="lossRecover">
    <div class="content_box">
      <div class="inner">
        <div class="contact_title">
          <h2>{{ $t('promotion.lossRecover.lossRecover') }}</h2>
          <p></p>
        </div>
        <div class="box">
          <div class="claimed_box" v-if="step === 1 || step === 3">
            <h3 class="subtitle">{{ $t('promotion.lossRecover.subtitle', { days: 7 }) }}</h3>
            <div class="desc">{{ $t('promotion.lossRecover.tnc', { days: 7 }) }}</div>
            <div class="desc">
              <i18n path="promotion.referFriends.tnc_v2">
                <template v-slot:link>
                  <a href="https://eu.vtmarkets.com/en-eu/promotions/other-offers/7-day-loss-recovery/" target="_blank">
                    {{ $t('promotion.referFriends.link') }}
                  </a>
                </template>
              </i18n>
            </div>
            <div class="desc-red bold" v-if="voucherInfoText && voucherInfoText.length > 0">
              <p>{{ $t(voucherInfoText) }}</p>
            </div>
            <div class="claimed bold" v-if="step === 3">{{ $t('promotion.lossRecover.claimed') }}</div>
            <div class="button_box">
              <el-button
                class="el-button blue_button"
                data-testid="claimNow"
                @click="claimNow"
                :disabled="!claimStatus"
              >
                {{ $t('promotion.lossRecover.claim') }}
              </el-button>
            </div>
            <div class="line" v-if="depositStatus"></div>
            <div class="desc" v-if="depositStatus">
              <p class="red bold">{{ $t('promotion.lossRecover.notification') }}</p>
            </div>
            <div class="desc-red" v-if="depositStatus">
              <el-button class="el-button blue_button" data-testid="claimNow" @click="depositNow">
                {{ $t('promotion.lossRecover.deposit') }}</el-button
              >
            </div>
          </div>
          <div class="account_box flex-center" v-if="step === 2">
            <el-form :model="form" ref="form" :rules="rules">
              <el-form-item class="labelTitle">
                <div>
                  <label>{{ $t('promotion.lossRecover.accNumber') }}</label>
                </div>
                <div class="row value">{{ mt4Account }}</div>
              </el-form-item>
              <div class="orderSelect">
                <SelectForm
                  v-model="form.tradeOrder"
                  :label="$t('promotion.lossRecover.selectYourTradeOrder')"
                  name="tradeOrder"
                  class="tradeOrder"
                  prop="tradeOrder"
                >
                  <el-option
                    v-for="(item, index) in trades"
                    :key="'trade-' + index"
                    :value="JSON.stringify(item)"
                    :label="getCurrencyLabel(item)"
                  ></el-option>
                </SelectForm>
              </div>
              <div class="amount">
                <numeric-input
                  ref="numberInput"
                  v-model="form.amount"
                  :precision="2"
                  :label="$t('promotion.lossRecover.amtAvailableToRevocer')"
                  name="amount"
                  :readOnly="true"
                ></numeric-input>
              </div>
              <div class="checkTnc_box flex-center">
                <el-form-item
                  prop="checkStatus"
                  :class="{
                    short_width: ['zh_CN', 'zh_TW', 'ja', 'th', 'ar'].includes(lang),
                    mid_width: ['ko'].includes(lang)
                  }"
                >
                  <el-checkbox v-model="form.checkStatus" data-testid="checkbox">
                    <i18n path="promotion.lossRecover.agreedTo">
                      <template v-slot:link>
                        <a
                          href="https://www.vtmarkets.com/en-eu/promotions/other-offers/7-day-loss-recovery/"
                          target="_blank"
                          data-testid="withdrawPolicy"
                          >{{ $t('promotion.lossRecover.tncs') }}</a
                        >
                      </template>
                    </i18n>
                  </el-checkbox>
                </el-form-item>
              </div>
              <div class="button_box">
                <el-button
                  class="el-button blue_button"
                  @click="submitForm"
                  :disabled="step === 3"
                  data-testid="claimNow"
                >
                  {{ $t('promotion.lossRecover.submit') }}</el-button
                >
              </div>
            </el-form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NumericInput from '@/components/NumericInput';
import { checkAgreedTNC } from '@/util/validation';
import mixin from '@/mixins/page/deposit';
import { apiGetCampaignParticipants, apiRedeemLossRecovery } from '@/resource';

const VoucherStatus = Object.freeze({
  VALID: 'VALID',
  REDEEMED: 'REDEEMED',
  EXPIRED: 'EXPIRED'
});

export default {
  name: 'lossRecover',
  components: { NumericInput },
  mixins: [mixin],
  data() {
    return {
      mt4Account: '',
      campaignId: 29,
      step: 1,
      claimStatus: false,
      depositStatus: false,
      voucherInfoText: '',
      form: {
        tradeOrder: '',
        amount: 0,
        participantId: '',
        checkStatus: false
      },
      trades: [],
      rules: {
        tradeOrder: [
          {
            required: true,
            trigger: 'change',
            message: this.$t('common.formValidation.tradeOrderReq')
          }
        ],
        checkStatus: [{ validator: checkAgreedTNC, trigger: 'change' }]
      }
    };
  },
  methods: {
    getCurrencyLabel(item) {
      return '#' + item.ticket + ' ' + item.currency + ' ' + item.profit;
    },
    depositNow() {
      this.$router.push({ name: 'depositFunds' });
    },
    getCampaignParticipants() {
      apiGetCampaignParticipants(this.campaignId)
        .then(resp => {
          if (resp.data.code == 0) {
            // disable claim button when status or redeemable is false
            if (!resp.data.data.status || !resp.data.data.redeemable) {
              this.claimStatus = false;
            }

            // display deposit desc & button when user is enrolled but did not reach deposited status
            if (resp.data.data.status == 'ENROLLED') {
              this.depositStatus = true;
              this.claimStatus = false;
            }

            switch (resp.data?.data?.voucher?.status) {
              // enable claim button when voucher status is valid
              case VoucherStatus.VALID:
                {
                  this.claimStatus = true;
                  this.trades = resp.data.data.trades;
                  this.form.amount = resp.data.data.voucher.amount;
                  this.mt4Account = resp.data.data.mt4Account;
                }
                break;
              // enable claimed text & disable claim button when voucher status is redeemed
              case VoucherStatus.REDEEMED:
                {
                  this.voucherInfoText = 'promotion.lossRecover.claimed';
                  this.claimStatus = false;
                }
                break;
              case VoucherStatus.EXPIRED:
                {
                  this.voucherInfoText = 'promotion.lossRecover.expired';
                  this.claimStatus = false;
                }
                break;
            }
          }
        })
        .catch(err => {
          console.log(err);
        });
    },
    claimNow() {
      this.step = 2;
    },
    submitRedeem() {
      return apiRedeemLossRecovery(
        {
          participantId: this.form.participantId,
          ticket: this.form.ticket,
          campaignId: this.campaignId
        },
        this.token
      );
    },
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.submitRedeem()
            .then(result => {
              if (result.data.code == 0) {
                this.$message({
                  message: this.$t('promotion.lossRecover.claimSuccess'),
                  type: 'success'
                });
                this.step = 1;
                this.getCampaignParticipants();
              } else {
                if (result.data.code != 562) this.errorMessage(this.$t('lossRecovery.default.failed'));
              }
            })
            .catch(err => {
              this.errorMessage(this.$t('lossRecovery.default.failed'));
            });
        } else {
          return false;
        }
      });
    }
  },
  computed: {
    lang() {
      return this.$store.state.common.lang;
    }
  },
  watch: {
    'form.tradeOrder'(value) {
      const jsonParse = JSON.parse(value);
      this.form.participantId = jsonParse.participantId;
      this.form.ticket = jsonParse.ticket;
    },
    trades(options) {
      if (options.length > 0) {
        this.form.tradeOrder = JSON.stringify(options[0]);
      }
    }
  },
  mounted() {
    this.getCampaignParticipants();
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/pages/lossRecover.scss';
</style>
